import { Database } from '@/types/supabase';
import { SupabaseClient } from '@supabase/supabase-js';

// 5-min TTL in-memory cache. Pinned to globalThis so Next.js's separate
// module graphs (route handlers vs. RSC) share ONE Map — otherwise an
// invalidate from a route handler can't bust the entry an RSC reads.
//
// Originally introduced in commit fec63172 to reduce Supabase egress
// ~80-90% during tournament weekends; do not remove without measuring.
const PERMISSION_CACHE_TTL_MS = 5 * 60 * 1000;

interface PermissionCacheEntry {
  data: {
    federationAdminRoles: Awaited<ReturnType<typeof permissionModel.getFederationAdminRoles>>;
    locationAdminRoles: Awaited<ReturnType<typeof permissionModel.getLocationAdminRoles>>;
    eventAdminRoles: Awaited<ReturnType<typeof permissionModel.getEventAdminRoles>>;
  };
  expiresAt: number;
}

const globalKey = Symbol.for('playpulse.permissionCache');
type GlobalWithCache = typeof globalThis & {
  [globalKey]?: Map<string, PermissionCacheEntry>;
};
const g = globalThis as GlobalWithCache;
const permissionCache: Map<string, PermissionCacheEntry> =
  g[globalKey] ?? new Map<string, PermissionCacheEntry>();
g[globalKey] = permissionCache;

// Test-only escape hatch so unit tests can assert on cache state without
// reaching across module instances. Not exported via the model surface.
export const __permissionCacheForTests = permissionCache;

export const permissionModel = {
  async getPermissions(client: SupabaseClient<Database>, userId: string) {
    const cached = permissionCache.get(userId);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.data;
    }

    const [federationAdminRoles, locationAdminRoles, eventAdminRoles] = await Promise.all([
      this.getFederationAdminRoles(client, userId),
      this.getLocationAdminRoles(client, userId),
      this.getEventAdminRoles(client, userId),
    ]);

    const result = {
      federationAdminRoles,
      locationAdminRoles,
      eventAdminRoles,
    };

    permissionCache.set(userId, {
      data: result,
      expiresAt: Date.now() + PERMISSION_CACHE_TTL_MS,
    });

    return result;
  },

  /**
   * Invalidate cached permissions. Pass a userId to drop a single entry
   * (after a role change), or no argument to clear everything
   * (only used by tests).
   *
   * The cache lives on globalThis so this delete is visible to every
   * module-instance Next.js may have produced via bundling.
   */
  invalidateCache(userId?: string) {
    if (userId) {
      permissionCache.delete(userId);
    } else {
      permissionCache.clear();
    }
  },

  async getFederationAdminRoles(client: SupabaseClient<Database>, userId: string) {
    const { data: adminRoles, error: adminError } = await client
      .from('federation_user')
      .select('role, federation_id')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .eq('status', 'active');
    if (adminError) return [];
    return adminRoles;
  },

  async getLocationAdminRoles(client: SupabaseClient<Database>, userId: string) {
    const { data: adminRoles, error: adminError } = await client
      .from('location_admin')
      .select(
        `
        role, 
        location_id,
        location!inner(federation_id)
      `,
      )
      .eq('user_id', userId)
      .eq('role', 'admin')
      .eq('status', 'active');
    if (adminError) return [];
    return adminRoles
      .filter(
        (role: { location: { federation_id: string | null } }) =>
          role.location.federation_id !== null,
      )
      .map((role) => ({
        role: role.role,
        location_id: role.location_id,
        federation_id: role.location.federation_id!,
      }));
  },

  /**
   * Pure function to fetch event admin roles for a user with flexible role filtering
   * @param client - Supabase client
   * @param userId - User ID
   * @param roles - Array of roles to filter by (e.g., ['admin'] or ['admin', 'referee'])
   * @returns Array of event admin roles
   */
  async fetchEventAdminRoles(client: SupabaseClient<Database>, userId: string, roles: string[]) {
    const { data: adminRoles, error: adminError } = await client
      .from('event_admin')
      .select(
        `
        role, 
        event_id,
        event!inner(federation_id)
      `,
      )
      .eq('user_id', userId)
      .in('role', roles)
      .eq('status', 'active');
    if (adminError) return [];
    return adminRoles
      .filter(
        (role: { event: { federation_id: string | null } }) => role.event.federation_id !== null,
      )
      .map((role) => ({
        role: role.role,
        event_id: role.event_id,
        federation_id: role.event.federation_id!,
      }));
  },

  async getEventAdminRoles(client: SupabaseClient<Database>, userId: string) {
    return this.fetchEventAdminRoles(client, userId, ['admin']);
  },

  /**
   * Get all event admin roles (including referees) for a user
   * This is separate from the general permissions system to avoid giving referees too much access
   */
  async getEventAdminRolesIncludingReferees(client: SupabaseClient<Database>, userId: string) {
    return this.fetchEventAdminRoles(client, userId, ['admin', 'referee']);
  },

  /**
   * Resolve which federation a user is an admin for.  Returns the first
   * matching federation_id from federation admin roles, falling back to
   * location admin roles.  Returns null if the user has no admin role.
   *
   * Use this when a route does NOT receive a federationId in the request
   * and needs to derive one from the user's roles (e.g. recalculate).
   */
  async resolveAdminFederationId(
    client: SupabaseClient<Database>,
    userId: string,
  ): Promise<string | null> {
    const { federationAdminRoles, locationAdminRoles } = await this.getPermissions(client, userId);
    if (federationAdminRoles.length > 0) return federationAdminRoles[0].federation_id;
    if (locationAdminRoles.length > 0) return locationAdminRoles[0].federation_id;
    return null;
  },

  // resource: some object with federation_id
  async hasPermission(
    client: SupabaseClient<Database>,
    userId: string,
    resourceType: string,
    resource: { id: string; federation_id: string },
  ) {
    const { federationAdminRoles, locationAdminRoles, eventAdminRoles } = await this.getPermissions(
      client,
      userId,
    );

    // if resource belongs to any of the federationAdminRoles, return true
    if (federationAdminRoles.some((role) => role.federation_id === resource.federation_id)) {
      return true;
    }

    if (resourceType === 'location') {
      return locationAdminRoles.some((role) => role.location_id === resource.id);
    }

    if (resourceType === 'event') {
      if (eventAdminRoles.some((role) => role.event_id === resource.id)) return true;
      // Location admins (role 'admin') manage events at their OWN location. The
      // resource carries no location_id, so look it up on this fallback path only
      // (same in-DB pattern as the 'user' branches below).
      if (locationAdminRoles.length > 0) {
        const { data: ev } = await client
          .from('event')
          .select('location_id')
          .eq('id', resource.id)
          .maybeSingle();
        const locationId = (ev as { location_id: string | null } | null)?.location_id ?? null;
        if (locationId && locationAdminRoles.some((role) => role.location_id === locationId)) {
          return true;
        }
      }
      return false;
    }

    // Location admins can manage matches within their federation
    if (resourceType === 'match') {
      return locationAdminRoles.some((role) => role.federation_id === resource.federation_id);
    }

    // Location admins can access players at their locations
    if (resourceType === 'user' && locationAdminRoles.length > 0) {
      const locationIds = locationAdminRoles.map((r) => r.location_id);
      const { data } = await client
        .from('player_location')
        .select('id')
        .eq('player_id', resource.id)
        .in('location_id', locationIds)
        .limit(1);
      if (data && data.length > 0) return true;
    }

    // Event admins can access players registered in events they admin
    if (resourceType === 'user' && eventAdminRoles.length > 0) {
      const eventIds = eventAdminRoles.map((r) => r.event_id);
      const { data: groups } = await client
        .from('event_group')
        .select('id')
        .in('event_id', eventIds);
      if (groups && groups.length > 0) {
        const groupIds = (groups as Array<{ id: string }>).map((g) => g.id);
        const { data: regs } = await client
          .from('event_group_registration')
          .select('id')
          .eq('player_id', resource.id)
          .in('event_group_id', groupIds)
          .limit(1);
        if (regs && regs.length > 0) return true;
      }
    }

    return false;
  },
};
